import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { useDefaultTerritories, useQueryDefaults } from '@/hooks/queries/defaults/useQueryDefaults'
import { orderSearchSchema, ordersExportQueryOptions, useOrders } from '@/hooks/queries/useOrders'
import { useTablePagination } from '@/hooks/useTablePagination'
import { isNova } from '@/config'
import { queryClient } from '@/main'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { FLAGGED_ORDERS_KEY } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import NovaOrderDataTable from '../-components/NovaOrderDataTable'
import OrderDataTable from '../-components/OrderDataTable'
import { mainlineOrdersData, novaOrdersData } from '../-components/ordersListShape'

export const Route = createFileRoute('/_auth/_modules/_app/orders/flagged/')({
  component: FlaggedOrdersPage,
  validateSearch: (search) => orderSearchSchema.parse(search),
})

function FlaggedOrdersPage() {
  const { t } = useTranslation('orders')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const defaultTerritories = useDefaultTerritories()

  useQueryDefaults({
    search,
    navigate,
    defaults: {
      status: ['HOLD'],
      territory: defaultTerritories,
    },
  })

  const { isPending, error, data } = useOrders({
    ...search,
    flagged: true,
    page: search.page ?? 1,
    source: search.source,
    pageSize,
  })

  const onSearch = (value: string) => {
    if (value) {
      navigate({ search: { ...search, keyword: value, page: 1 } })
    } else {
      navigate({ search: { ...search, keyword: undefined, page: 1 } })
    }
  }

  const getCsvExport = () =>
    queryClient.ensureQueryData(ordersExportQueryOptions({ ...search, type: FLAGGED_ORDERS_KEY }))

  // Only where this page differs from each table's own defaults. The split-actuals ids exist on the
  // mainline table alone; naming them here is a no-op on the Nova table, which offers only the total.
  const columnConfig = {
    initialVisibility: {
      // Nova only: every row here is HOLD, since this page queries `status: ['HOLD']`, so the column
      // is a constant. Mainline keeps showing it, unchanged.
      ...(isNova ? { status: false } : {}),
      lastComment: true,
      outletSubtype: true,
      actuals: true,
      actualFmc: false,
      actualVuse: false,
      actualOther: false,
      offRoute: false,
      sapErp: false,
    },
  }

  const tableProps = {
    isPending,
    error,
    route: Route,
    columnConfig,
    pageSize,
    onPageSizeChange,
    onSearch,
  }

  return (
    <>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.FLAGGED_ORDERS}
        title={t('tutorials.flaggedOrders.dialog.title')}
        guideUrl="/orders/flagged-orders"
      >
        {t('tutorials.flaggedOrders.dialog.description')}
      </IntroductionDialog>

      <PageContainer maxWidth="wide">
        <Spread>
          <PageHeader>{t('flaggedOrdersPage.heading')}</PageHeader>
          <ExportCsvButton getCsvExport={getCsvExport} rowCount={data?.rowCount ?? 0} />
        </Spread>

        <Card>
          <CardContent className="py-6">
            {/* Same fork as the all-orders list, and for the same reason: the Nova row shape and
                its coupling-state filter have no mainline equivalent. */}
            {isNova ? (
              <NovaOrderDataTable {...tableProps} data={novaOrdersData(data)} />
            ) : (
              <OrderDataTable {...tableProps} data={mainlineOrdersData(data)} />
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </>
  )
}
